import React, { useState } from 'react';
import styles from './DebtProductOverview.module.scss';
import clsx from 'clsx';
import ProbabilityBar from '../ProbabilityBar/ProbabilityBar';
import { debtProdTermsError, debtProdTermsOk, debtProductsEmpty, editProfileIconPurple } from '../../../../assets/hostedassets';
import { DebtProductRowData, TermsObj } from '../../debtProductTypes';
import SideDrawer from '../../../../common/_custom/SideDrawer/SideDrawer';
import closeIcon from '../../../../common/_custom/Modal/close.svg';
import { fetchUserFromOrgId } from '../../../../store/investor/investorApiHelper';
import { fetchInternalSummonAicaData, InternalSummonAicaDataPayload, InternalSummonUserType } from '../../../../store/user/actions';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { getPlatformUserId, openUserSummonTab } from '../../../../utils/utils';
import { INVESTOR_ROUTES, LENDER_ROUTES } from '../../../../utils/constants/routesConst';
import { toast } from 'react-toastify';
import { isEmpty } from 'lodash';
import { IMPORTANCE_LEVELS, EDIT_PROFILE } from '../../../../common/ManageDealDrawer/DealDrawerConstants';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';
import { useFeatureVisibility } from '../../../../utils/useVisibilityConfig';
import { Button } from '../../../../common/_custom';
import { setDealDrawer } from '../../../../store/investor/action';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import { renderTooltipContent } from '../../../../common/_custom/Tooltip/tooltipContentUtils';
import infoIcon from '../../../../assets/info.svg';
import { formatDateByFormat, DATE_FORMATS } from '../../../../utils/dateUtils';
import { CollapsableDropdown } from '../../../../modules/Investor/Deals/components/DealsList/CollapsableDropdown/CollapsableDropdown';
import AskAicaQuestionBadge from '../../../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';
import { useLenderIpGate } from '../../hooks/useLenderIpGate';
import { FLOW_KEY_DCM } from '../../../../store/dashboard/askAica/constants';

export interface ProbabilitySnapshot {
    referenceId: string;
    productId: string;
    productName?: string;
    probability: number;
    details?: { terms?: Record<string, TermsObj>; eligibility?: Record<string, TermsObj> };
    stage: string;
    capturedAt?: number;
    updatedBy?: string;
    updatedByName?: string;
    snapshotType?: string;
}

interface DebtProductOverviewProps {
    data: DebtProductRowData;
    closeModal: Function;
    percentageCompletion?: {
        borrowerFinancials?: {
            actualData?: number;
            discussion?: number;
        };
    };
    activeCompletionPercentage?: number;
    selectedCompany?: {
        orgName?: string;
    };
    registerMixpanelForEditProfileClicked?: (source: string) => void;
    isDealView?: boolean;
    dealSharedInfo?: {
        sharedAt: number;
        sharedBy: string;
    };
    handleActionWithUnsavedCheck?: (action: () => void) => void;
    snapshots?: ProbabilitySnapshot[];
    companyName?: string;
    productName?: string;
}

function DebtProductOverview({
    data,
    closeModal,
    percentageCompletion,
    activeCompletionPercentage,
    selectedCompany,
    registerMixpanelForEditProfileClicked,
    isDealView,
    dealSharedInfo,
    handleActionWithUnsavedCheck,
    snapshots,
    companyName,
    productName: productNameProp,
}: DebtProductOverviewProps) {
    const dispatch = useAppDispatch();
    const { editPreferencesVisibility, showImportanceColumnVisibility, showPecValuesVisibility, editProfileVisibility } =
        useFeatureVisibility({
            editPreferencesVisibility: VISIBILITY_KEYS.LENDER_MANAGEMENT.SUMMON,
            showImportanceColumnVisibility: VISIBILITY_KEYS.DEBT_PRODUCT.SHOW_IMPORTANCE_COLUMN,
            showPecValuesVisibility: VISIBILITY_KEYS.DEBT_PRODUCT.SHOW_PEC_VALUES,
            editProfileVisibility: VISIBILITY_KEYS.DEBT_PRODUCT.EDIT_PROFILE,
        });

    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const dealStatusConfig = useAppSelector((state) => state.investor.deals.dealStatusConfig);
    const aicaDebtProductConfig = useAppSelector((state) => state.investor.aicaDebtProductConfig);
    const tentativeTermsTableHeader = aicaDebtProductConfig?.debtProductOverviewTableHeaders?.tentativeTerms;
    const eligibilityCriteriaTableHeader = aicaDebtProductConfig?.debtProductOverviewTableHeaders?.eligibilityCriteria;
    const eligibilityValueMapping = aicaDebtProductConfig?.valueMapping?.eligibility;
    const showEditPreferences = editPreferencesVisibility.show;
    const showImportanceColumn = showImportanceColumnVisibility.show;
    const showPecValues = showPecValuesVisibility.show;

    const isSnapshotListMode = Array.isArray(snapshots) && snapshots.length > 0;

    const [expandedIndex, setExpandedIndex] = useState<number | null>(
        isSnapshotListMode && snapshots?.length ? snapshots.length - 1 : null,
    );
    const hasLenderIp = useLenderIpGate(data?.lenderId);

    const overviewData = data?.details;
    const termsData = overviewData?.terms || {};
    const eligibilityData = overviewData?.eligibility || {};

    const getStatusIcon = (status) => {
        switch (status) {
            case true:
                return debtProdTermsOk;
            case false:
            default:
                return debtProdTermsError;
        }
    };

    const getImportanceIcon = (importanceLevel?: string) => {
        const importance = IMPORTANCE_LEVELS.find((level) => level.id === importanceLevel);
        if (!importance || !importance.icon) return null;
        const sizeStyle = { width: '14px', height: '14px', marginTop: 0 };

        switch (typeof importance.icon) {
            case 'string':
                return <img src={importance.icon} alt={importance.label || 'Importance icon'} style={sizeStyle} />;
            case 'object':
                return React.cloneElement(importance.icon, { style: sizeStyle });
            default:
                return null;
        }
    };

    const renderValueRow = (value: TermsObj) => {
        if (value?.TYPE === 'TEXT') {
            return (
                <div className={styles.Value}>
                    <div className={styles.CalcValue}>{value?.COMPANY_VALUE}</div>
                </div>
            );
        } else if (value?.MATCHED?.length || value?.UNMATCHED?.length) {
            return (
                <div className={styles.ValueRows}>
                    {value?.MATCHED?.map((str, i) => (
                        <div className={styles.ValueRow} key={i}>
                            <img src={debtProdTermsOk} alt="Matched" height={'14px'} />
                            {str}
                        </div>
                    ))}
                    {value?.UNMATCHED?.map((str, i) => (
                        <div className={styles.ValueRow} key={i}>
                            <img src={debtProdTermsError} alt="Not Matched" height={'14px'} />
                            {str}
                        </div>
                    ))}
                </div>
            );
        }
        return (
            <div className={styles.Value}>
                <img src={getStatusIcon(value.STATUS)} alt="Status" height={'14px'} />
                <div className={styles.CalcValue}>{value?.COMPANY_VALUE}</div>
                {showPecValues && <div className={styles.OriginalValue}>{value?.PEC_VALUE ? `/ ${value?.PEC_VALUE}` : ''}</div>}
            </div>
        );
    };

    const openEditProfileDrawer = (source: string) => {
        closeModal?.();
        dispatch(
            setDealDrawer({
                open: true,
                drawerLabel: EDIT_PROFILE,
                companyName: selectedCompany?.orgName,
            }),
        );
        registerMixpanelForEditProfileClicked?.(source);
    };

    const handleEditProfile = (source: string) => {
        if (handleActionWithUnsavedCheck) {
            handleActionWithUnsavedCheck(() => openEditProfileDrawer(source));
        } else {
            openEditProfileDrawer(source);
        }
    };

    const summonUserEditPreferences = async () => {
        if (!data?.lenderId || data?.lenderId === data?.aggregatorId) {
            window.open(`${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.MANAGE_ELIGIBILITY}`, '_blank');
            return;
        }
        const users = await fetchUserFromOrgId(data?.lenderId, InternalSummonUserType.LENDER);
        if (Array.isArray(users) && users.length) {
            const summonPayload: InternalSummonAicaDataPayload = {
                parentOrgId: platformData?.investorOrgId as string,
                parentEmailId: users[0].emailId,
                userId: users[0].userId,
                userType: InternalSummonUserType.LENDER,
                requesterUserId: getPlatformUserId(),
            };
            dispatch(
                fetchInternalSummonAicaData(summonPayload, {
                    onSuccess: (userData) => {
                        openUserSummonTab({
                            ...userData,
                            orgId: platformData?.investorOrgId,
                            referrer: LENDER_ROUTES.MANAGE_ELIGIBILITY,
                        });
                    },
                    onError: () => {
                        toast.error('Failed to summon user');
                    },
                }),
            );
        }
    };

    const renderTermsAndEligibility = (terms: Record<string, TermsObj>, eligibility: Record<string, TermsObj>) => (
        <>
            {!!Object.keys(terms).length && (
                <div className={styles.Table}>
                    <div className={styles.TableHeader}>
                        {tentativeTermsTableHeader?.map((item: string, index: number) => {
                            if (index === 2 && !showImportanceColumn) return null;
                            return (
                                <div key={index} className={styles.Metric}>
                                    {item}
                                </div>
                            );
                        })}
                    </div>
                    {Object.entries(terms).map(([key, val]: [string, TermsObj]) => (
                        <div key={key} className={styles.Row}>
                            <div className={styles.Metric}>{val.text}</div>
                            {renderValueRow(val)}
                            {showImportanceColumn && (
                                <div className={clsx(styles.Metric, styles.ImportanceMetric)}>
                                    {getImportanceIcon(val?.IMPORTANCE_OF_METRIC_FROM_PEC)} {val?.IMPORTANCE_OF_METRIC_FROM_PEC}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {!!Object.keys(eligibility).length && (
                <div className={styles.Table}>
                    <div className={styles.TableHeader}>
                        {eligibilityCriteriaTableHeader?.map((item: string, index: number) => {
                            if (index === 2 && !showImportanceColumn) return null;
                            return (
                                <div key={index} className={styles.Metric}>
                                    {item}
                                </div>
                            );
                        })}
                    </div>
                    {Object.entries(eligibility).map(([key, val]: [string, TermsObj]) => {
                        const tooltipContent = (eligibilityValueMapping?.[key] as { key: string; text: string; tooltip?: string })?.tooltip;
                        return (
                            <div key={key} className={styles.Row}>
                                <div className={styles.Metric}>
                                    {val.text}
                                    {tooltipContent && (
                                        <Tooltip content={renderTooltipContent(tooltipContent)} direction="top" fitContent usePortal={true}>
                                            <img src={infoIcon} alt="info" className={styles.InfoIcon} />
                                        </Tooltip>
                                    )}
                                </div>
                                {renderValueRow(val)}
                                {showImportanceColumn && (
                                    <div className={clsx(styles.Metric, styles.ImportanceMetric)}>
                                        {getImportanceIcon(val?.IMPORTANCE_OF_METRIC_FROM_PEC)} {val?.IMPORTANCE_OF_METRIC_FROM_PEC}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );

    // ─── Snapshot list mode (Probability History) ─────────────────────
    if (isSnapshotListMode) {
        const statusOptionsMap = dealStatusConfig?.statusDropdownOptionsMap || {};

        const DEFAULT_STATUS = { label: '-', backgroundColor: '#F0F0F0', labelColor: '#666' };
        const getSnapshotStatusOption = (stage?: string) => {
            if (!stage) return DEFAULT_STATUS;
            const opt = statusOptionsMap[stage];
            if (opt) {
                return {
                    ...opt,
                    backgroundColor: opt.backgroundColor || DEFAULT_STATUS.backgroundColor,
                    labelColor: opt.labelColor || DEFAULT_STATUS.labelColor,
                };
            }
            return {
                ...DEFAULT_STATUS,
                label: stage
                    .replace(/_/g, ' ')
                    .toLowerCase()
                    .replace(/\b\w/g, (c) => c.toUpperCase()),
            };
        };

        return (
            <SideDrawer width={'50%'} onClose={() => closeModal()} allowClose={false}>
                <div className={clsx(styles.DebtProductOverview, styles.SnapshotMode)}>
                    <div className={styles.Header}>
                        <div className={styles.HeaderLeft}>
                            <span className={clsx(styles.ProductNameContainer, styles.NoBorderRight)}>
                                Probability History
                                {companyName && <span className={styles.LenderName}> | {companyName}</span>}
                            </span>
                        </div>
                        <div className={styles.ProbabilityBar}>
                            <img
                                className={styles.CloseIcon}
                                src={closeIcon}
                                alt="close"
                                height="20px"
                                width="20px"
                                onClick={() => closeModal?.()}
                            />
                        </div>
                    </div>

                    <div className={styles.SnapshotList}>
                        {snapshots!.map((snapshot, index) => {
                            const statusOption = getSnapshotStatusOption(snapshot.stage);
                            const snapshotProductName = snapshot.productName || snapshot.productId || '-';
                            const isExpanded = expandedIndex === index;

                            return (
                                <div key={index} className={styles.SnapshotDropdownContainer}>
                                    <CollapsableDropdown
                                        triggerContent={() => (
                                            <div className={styles.SnapshotTrigger}>
                                                <div className={styles.SnapshotTriggerLeft}>
                                                    <span className={styles.SnapshotProductName} title={snapshotProductName}>
                                                        {snapshotProductName}
                                                    </span>
                                                    <div
                                                        className={styles.StageBadge}
                                                        style={{
                                                            backgroundColor: statusOption.backgroundColor,
                                                            color: statusOption.labelColor,
                                                        }}
                                                    >
                                                        {statusOption.label}
                                                    </div>
                                                </div>
                                                <div className={styles.SnapshotTriggerRight}>
                                                    <span className={styles.SnapshotMeta}>
                                                        {snapshot.capturedAt &&
                                                            formatDateByFormat(
                                                                snapshot.capturedAt,
                                                                DATE_FORMATS.TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY,
                                                            )}
                                                        {(snapshot.updatedByName || snapshot.updatedBy) &&
                                                            ` by ${snapshot.updatedByName || snapshot.updatedBy}`}
                                                    </span>
                                                    <div className={styles.SnapshotProbabilityContainer}>
                                                        <div className={styles.SnapshotProbability}>
                                                            <ProbabilityBar probability={String(snapshot.probability ?? 0)} />
                                                        </div>
                                                        <Tooltip
                                                            content="Captured when the mandate entered this stage."
                                                            direction="left"
                                                            usePortal={true}
                                                            contentCss={{
                                                                position: 'relative',
                                                                left: '10px',
                                                                width: 'fit-content',
                                                                maxWidth: '200px',
                                                            }}
                                                        >
                                                            <img src={infoIcon} alt="info" className={styles.SnapshotInfoIcon} />
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        isExpanded={isExpanded}
                                        onToggle={(expanded) => setExpandedIndex(expanded ? index : null)}
                                    >
                                        <div className={clsx(styles.Body, styles.SnapshotBody)}>
                                            {renderTermsAndEligibility(snapshot.details?.terms || {}, snapshot.details?.eligibility || {})}
                                        </div>
                                    </CollapsableDropdown>
                                </div>
                            );
                        })}
                    </div>
                    <div className={styles.Footer}>
                        {showEditPreferences && (
                            <button
                                className={styles.EditPreferences}
                                onClick={
                                    isDealView
                                        ? summonUserEditPreferences
                                        : () => window.open(`${INVESTOR_ROUTES.APP}/${INVESTOR_ROUTES.MANAGE_ELIGIBILITY}`, '_blank')
                                }
                            >
                                Edit Investment Preferences
                            </button>
                        )}
                    </div>
                </div>
            </SideDrawer>
        );
    }

    // ─── Single snapshot mode (existing behavior) ─────────────────────
    return (
        <SideDrawer width={hasLenderIp ? '75%' : '50%'} onClose={() => closeModal()} allowClose={false}>
            <div className={styles.DebtProductOverview}>
                <div className={styles.Header}>
                    <div className={styles.HeaderLeft}>
                        <span className={styles.ProductNameContainer}>
                            {data.productName || '-'}
                            {!!data?.lenderName && <span className={styles.LenderName}> | {data.lenderName}</span>}
                        </span>
                        <ProbabilityBar probability={data?.probability} />
                    </div>
                    <div className={styles.ProbabilityBar}>
                        {isDealView ? (
                            !isEmpty(dealSharedInfo) ? (
                                <span className={styles.SharedAtText}>
                                    Shared at{' '}
                                    {formatDateByFormat(dealSharedInfo.sharedAt, DATE_FORMATS.TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY)} by{' '}
                                    {dealSharedInfo.sharedBy}
                                </span>
                            ) : null
                        ) : (
                            editProfileVisibility.show && (
                                <Button
                                    text={
                                        <div className={styles.EditProfileButtonText}>
                                            <span>Edit Profile</span>
                                            {activeCompletionPercentage !== undefined && (
                                                <div className={styles.PercentageCompletion}>{activeCompletionPercentage}%</div>
                                            )}
                                        </div>
                                    }
                                    onClick={() => handleEditProfile('Kebab Menu')}
                                    variant="white"
                                    style={{ marginTop: '0', borderColor: 'var(--primary-text-colour)' }}
                                    icon={editProfileIconPurple}
                                />
                            )
                        )}
                        {hasLenderIp && !!data.lenderName && !!data.productId && !!data.productName && (
                            <AskAicaQuestionBadge
                                question="Evaluate against detailed lender policy"
                                prompt={`Evaluate ${data.productName} for this company against ${data.lenderName}'s detailed lender policy.`}
                                flowKey={FLOW_KEY_DCM}
                                agentInput={{
                                    lender_org_id: data.lenderId!,
                                    product_id: data.productId,
                                    product_name: data.productName,
                                }}
                            />
                        )}
                        <img
                            className={styles.CloseIcon}
                            src={closeIcon}
                            alt="close"
                            height="20px"
                            width="20px"
                            onClick={() => closeModal?.()}
                        />
                    </div>
                </div>

                <div className={styles.Body}>{renderTermsAndEligibility(termsData, eligibilityData)}</div>
                <div className={styles.Footer}>
                    {showEditPreferences &&
                        (isEmpty(overviewData) ? (
                            <div className={styles.CenterEmptyContainer}>
                                <img src={debtProductsEmpty} alt={''} className={styles.EmptyImageProp} />
                                <button className={styles.EditPreferences} onClick={summonUserEditPreferences}>
                                    Edit Investment Preferences
                                </button>
                            </div>
                        ) : (
                            <button className={styles.EditPreferences} onClick={summonUserEditPreferences}>
                                Edit Investment Preferences
                            </button>
                        ))}
                </div>
            </div>
        </SideDrawer>
    );
}

export default DebtProductOverview;
